import { useState, useEffect } from 'react';
import API from '../../Services/api';
import L from 'leaflet';
import 'leaflet-control-geocoder';

const defaultItems = [
  { name: 'Answering Machines', points: 10 },
  { name: 'Laptops', points: 10 },
  { name: 'Automotive Electronics', points: 10 },
];

function AddCollectionPoint({ onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    longitude: '',
    latitude: '',
    acceptedItems: defaultItems.map(i => ({ ...i })),
  });
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [isAddressValid, setIsAddressValid] = useState(true);
  const [loading, setLoading] = useState(false);
  const [geocoding, setGeocoding] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === 'address') setIsAddressValid(true);
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.acceptedItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setFormData({ ...formData, acceptedItems: updatedItems });
  };

  const handleToggleItem = (index) => {
    const updatedItems = [...formData.acceptedItems];
    updatedItems[index] = { ...updatedItems[index], name: updatedItems[index].name ? '' : (defaultItems[index]?.name || '') };
    setFormData({ ...formData, acceptedItems: updatedItems });
  };

  const handleAddNewItem = () => {
    setFormData({ ...formData, acceptedItems: [...formData.acceptedItems, { name: '', points: 10 }] });
  };

  const handleRemoveItem = (index) => {
    const updatedItems = formData.acceptedItems.filter((_, i) => i !== index);
    setFormData({ ...formData, acceptedItems: updatedItems });
  };

  const handleGeocode = async () => {
    if (!formData.address.trim()) return;
    setGeocoding(true);
    const geocoder = L.Control.Geocoder.nominatim();
    geocoder.geocode(formData.address, (results) => {
      setGeocoding(false);
      if (results.length > 0) {
        const { lat, lng } = results[0].center;
        setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
        setIsAddressValid(true);
      } else {
        setFormData(prev => ({ ...prev, latitude: '', longitude: '' }));
        setIsAddressValid(false);
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await API.post('/collection-points/add', {
        ...formData,
        acceptedItems: formData.acceptedItems.filter(item => item.name && item.points),
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setMessage('Collection point added successfully!');
      setFormData({ name: '', address: '', longitude: '', latitude: '', acceptedItems: defaultItems.map(i => ({ ...i })) });
      if (onSuccess) setTimeout(onSuccess, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Error adding collection point. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = formData.name && formData.address && formData.latitude && formData.longitude;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="bg-gradient-to-r from-[#004d3f] to-[#006b57] px-6 py-5">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <i className="fas fa-plus-circle"></i>
          Add New Collection Point
        </h2>
        <p className="text-green-200 text-sm mt-1">Register a new e-waste recycling centre</p>
      </div>

      <div className="p-6">
        {message && (
          <div className="flex items-center gap-3 bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 mb-6">
            <i className="fas fa-check-circle text-green-500"></i>
            <span className="text-sm font-medium">{message}</span>
          </div>
        )}
        {error && (
          <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-6">
            <i className="fas fa-exclamation-circle text-red-500"></i>
            <span className="text-sm font-medium">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Centre Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="name"
                placeholder="e.g. City Recycling Hub"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#004d3f]/30 focus:border-[#004d3f] transition-colors"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Address <span className="text-red-500">*</span></label>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="address"
                  placeholder="Full address"
                  value={formData.address}
                  onChange={handleChange}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#004d3f]/30 focus:border-[#004d3f] transition-colors"
                  required
                />
                <button
                  type="button"
                  onClick={handleGeocode}
                  disabled={geocoding || !formData.address.trim()}
                  className="px-4 py-2.5 bg-[#004d3f] text-white rounded-xl text-sm font-medium hover:bg-[#003d32] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 whitespace-nowrap transition-colors"
                >
                  {geocoding ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-search-location"></i>}
                  Locate
                </button>
              </div>
              {!isAddressValid && (
                <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                  <i className="fas fa-exclamation-triangle"></i>
                  Address not found. Enter coordinates manually.
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Latitude <span className="text-red-500">*</span></label>
              <input
                type="number"
                name="latitude"
                placeholder="Auto-filled from address"
                value={formData.latitude}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#004d3f]/30 focus:border-[#004d3f] transition-colors bg-gray-50"
                required
                step="any"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Longitude <span className="text-red-500">*</span></label>
              <input
                type="number"
                name="longitude"
                placeholder="Auto-filled from address"
                value={formData.longitude}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#004d3f]/30 focus:border-[#004d3f] transition-colors bg-gray-50"
                required
                step="any"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-700">Accepted Items & Points</h3>
                <p className="text-xs text-gray-400 mt-0.5">Check items accepted and set reward points</p>
              </div>
              <button
                type="button"
                onClick={handleAddNewItem}
                className="flex items-center gap-1.5 text-sm text-[#004d3f] font-medium hover:text-[#003d32] transition-colors"
              >
                <i className="fas fa-plus-circle"></i>
                Add Item
              </button>
            </div>

            <div className="space-y-2 border border-gray-100 rounded-xl p-4 bg-gray-50 max-h-64 overflow-y-auto">
              {formData.acceptedItems.map((item, index) => (
                <div key={index} className="flex items-center gap-3 bg-white rounded-lg px-3 py-2.5 border border-gray-100">
                  <input
                    type="checkbox"
                    checked={!!item.name}
                    onChange={() => handleToggleItem(index)}
                    className="w-4 h-4 accent-[#004d3f] flex-shrink-0"
                  />
                  <input
                    type="text"
                    placeholder="Item name"
                    value={item.name}
                    onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                    className="flex-1 text-sm border-0 focus:outline-none focus:ring-0 bg-transparent min-w-0"
                  />
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <input
                      type="number"
                      placeholder="Pts"
                      value={item.points}
                      onChange={(e) => handleItemChange(index, 'points', e.target.value)}
                      className="w-16 text-sm border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#004d3f]/30 text-center"
                    />
                    <span className="text-xs text-gray-400">pts</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveItem(index)}
                    className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                  >
                    <i className="fas fa-times text-xs"></i>
                  </button>
                </div>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={!isFormValid || loading}
            className={`w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-200 ${
              isFormValid && !loading
                ? 'bg-[#004d3f] text-white hover:bg-[#003d32] shadow-sm'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {loading ? (
              <><i className="fas fa-spinner fa-spin"></i> Adding...</>
            ) : (
              <><i className="fas fa-plus"></i> Add Collection Point</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default AddCollectionPoint;
